import { ApiQueryParams, PaginatedResponse } from './models';

/**
 * Recursively fetches all pages from a paginated API endpoint.
 *
 * @param fetchFn - The fetch function that returns a paginated response
 * @param queryParams - The query parameters to pass to the fetch function
 * @param pageSize - The number of items per page (default: 100)
 * @returns Promise with all results combined from all pages
 *
 * @example
 * ```typescript
 * const allAttributes = await fetchAllPages(
 *     (params) => AttributeClient.fetchAttributes(params),
 *     { account_id: currentAccount }
 * );
 * ```
 */
export async function fetchAllPages<T, Q extends string | number = string>(
    fetchFn: (queryParams: ApiQueryParams<Q>) => Promise<PaginatedResponse<T>>,
    queryParams: ApiQueryParams<Q> = {},
    pageSize: string | string[] = '100'
): Promise<T[]> {
    let allResults: T[] = [];
    let page = 0;
    const size = Array.isArray(pageSize) ? pageSize[0] : pageSize;

    while (true) {
        const res = await fetchFn({
            ...queryParams,
            index: page.toString(),
            size
        } as ApiQueryParams<Q>);

        allResults = [...allResults, ...res.results];

        // Prefer count over "full page" checks — APIs may cap page size below the requested size.
        if (res.results.length === 0 || allResults.length >= res.count) {
            break;
        }
        page += 1;
    }

    return allResults;
}
